if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();
        this.form = this.querySelector('form');
        this.form.querySelector('[name=id]').disabled = false;

        /* -------------------------------------------------------------
         * Gift-box debugging utilities (shared with gift-box-add-to-cart.js)
         * ------------------------------------------------------------- */
        if (typeof window.__GIFTPROPS_DEBUG__ === 'undefined') {
          // Default to off in production; can be toggled in DevTools.
          window.__GIFTPROPS_DEBUG__ = false;
        }

        window.logGiftboxDebug = window.logGiftboxDebug || function (msg, payload) {
          if (!window.__GIFTPROPS_DEBUG__) return;
          console.warn(`%c[GiftBox Debug] ${msg}`, 'color:#8A2BE2;font-weight:bold', payload);
        };

        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');
        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();
        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');
        let productIsPersonalised = false;
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];
        let formData = new FormData(this.form);

        // Debug: log initial add-to-cart payload (before any scrub)
        window.logGiftboxDebug('Initial FormData from ProductForm', Array.from(formData.entries()));

        /*
         * ---------------------------------------------
         * Remove orphaned gift-box line-item for contexts
         * ---------------------------------------------
         * PDP normal add-to-cart  → scrub when _hasGiftBox !== 'true'
         * PLP quick-add (inside <quick-add-modal>) → same scrub, but we use
         *    a stricter selector because quick-add HTML can be cloned and the
         *    disabled attribute may be lost.
         */

        const fromQuickAdd = Boolean(this.closest('quick-add-modal'));
        const giftingEnabled = Boolean(this.closest('product-form') && this.closest('product-form').dataset.gifting === 'true');

        const hasGiftBox = formData.get('properties[_hasGiftBox]') === 'true';
        
        // Also check if gift box is actually confirmed (selected-gifting-options is visible)
        const selectedGiftingOptions = document.getElementById('selected-gifting-options');
        const isGiftBoxConfirmed = selectedGiftingOptions && selectedGiftingOptions.style.display !== 'none';
        const giftBoxActuallySelected = hasGiftBox && isGiftBoxConfirmed;

        // Always scrub items[ entries if gift box is not confirmed, or if gifting is enabled
        // This prevents disabled inputs or stale values from adding gift boxes unintentionally
        if (giftingEnabled) {
          const cleaned = new FormData();
          let itemsEntriesFound = false;
          const itemsKeysSeen = new Set(); // Track items[ keys to prevent duplicates
          
          for (let [key, value] of formData.entries()) {
            // Always remove items[ entries (gift box addons) unless gift box is explicitly confirmed
            if (key.startsWith('items[')) {
              itemsEntriesFound = true;
              
              if (!giftBoxActuallySelected) {
                // Skip adding items[ entries if gift box not confirmed
                window.logGiftboxDebug('Removing items[] entry (gift box not confirmed)', { key, value });
                continue;
              }
              
              // If gift box IS confirmed, check for duplicates before including
              if (itemsKeysSeen.has(key)) {
                window.logGiftboxDebug('Duplicate items[] entry detected and skipped', { key, value });
                continue; // Skip duplicate items[ entries
              }
              
              itemsKeysSeen.add(key);
              cleaned.append(key, value);
              continue;
            }
            
            // Remove gift box properties if gift box is not confirmed
            if (!giftBoxActuallySelected) {
              if (key === 'properties[_giftProductHandle]' || 
                  key === 'properties[_giftPid]' || 
                  key === 'properties[_hasGiftBox]') {
                window.logGiftboxDebug('Removing gift box property (not confirmed)', { key, value });
                continue;
              }
            }
            
            // Keep _groupId if personalization is present (it's needed for linking)
            if (key === 'properties[_groupId]') {
              cleaned.append(key, value);
              continue;
            }
            
            // Drop empty top-level id
            if (key === 'id' && (!value || value === '')) continue;
            
            cleaned.append(key, value);
          }
          
          if (itemsEntriesFound && !giftBoxActuallySelected) {
            window.logGiftboxDebug('Gift box items[] entries were present but removed - gift box not confirmed');
          }
          
          if (itemsKeysSeen.size > 0 && giftBoxActuallySelected) {
            window.logGiftboxDebug('Gift box items[] entries included in form submission', Array.from(itemsKeysSeen));
          }
          
          formData = cleaned;

          window.logGiftboxDebug('FormData after gift-box scrub', Array.from(formData.entries()));
        }
        // replace body later

        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }

        // Ensure a valid variant id is present before submitting. Some edge cases
        // (e.g. cloned quick-add forms or disabled inputs being toggled) can
        // result in FormData missing the `id` field, which causes a 400 from
        // /cart/add. Prefer the value from the hidden variant input.
        if (!formData.get('id') || formData.get('id') === '') {
          // 1) Hidden input[name="id"] inside the form
          let recoveredId = this.form.querySelector('input[name="id"]')?.value;
          // 2) Alternate hidden field with class .product-variant-id
          if (!recoveredId) recoveredId = this.form.querySelector('.product-variant-id')?.value;
          // 3) Submit button data attribute set by Liquid
          if (!recoveredId) recoveredId = this.submitButton?.dataset?.prodVariantId;
          // 4) Variant id in URL (e.g., ?variant=123)
          if (!recoveredId) recoveredId = new URLSearchParams(window.location.search).get('variant');

          if (recoveredId) {
            // Coerce to numeric string to avoid any stray whitespace or types
            const coerced = String(parseInt(recoveredId, 10));
            formData.set('id', coerced);
            window.logGiftboxDebug('Repaired missing variant id in FormData', coerced);
          } else {
            window.logGiftboxDebug('Variant id still missing before /cart/add. FormData keys', Array.from(formData.keys()));
          }
        }

        const currentGroupId = formData.get('properties[_groupId]');
        const groupId = currentGroupId || Date.now().toString();

        if (
          document.querySelector('personalisation-drawer') &&
          Boolean(formData.get(`properties[${window.personalisationStrings.initialsProperty}]`))
        ) {
          productIsPersonalised = true;

          if (!currentGroupId) {
            formData.append('properties[_groupId]', groupId);
          }
        }

        // Log the final payload (safe view) before submit
        try {
          const safeEntries = Array.from(formData.entries()).map(([k, v]) => [k, typeof v === 'string' ? v : '[Blob]']);
          const idsOnly = safeEntries.filter(([k]) => k === 'id' || k.endsWith('[id]'));
          window.logGiftboxDebug('About to POST /cart/add – final FormData', safeEntries);
          window.logGiftboxDebug('FormData ids', idsOnly);
        } catch (e) {}

        config.body = formData;

        window.logGiftboxDebug('POST →', routes.cart_add_url);
        fetch(`${routes.cart_add_url}`, config)
          .then(async (response) => {
            const clone = response.clone();
            const text = await clone.text();
            window.logGiftboxDebug('Response status from /cart/add', { ok: response.ok, status: response.status, body: text });
            try { return JSON.parse(text); } catch (_) { return { status: !response.ok, description: text || 'Non-JSON response' }; }
          })
          .then(async (initialResponse) => {
            if (productIsPersonalised) {
              await this.addMonogramProductToCart(groupId);
            }

            let response = initialResponse;

            if (response.status) {
              // If the platform reports missing id, attempt a JSON fallback
              const message = response.message || response.description || '';
              const idForFallback = formData.get('id');
              if (idForFallback && /missing|invalid\: id/i.test(message || '')) {
                window.logGiftboxDebug('Falling back to JSON /cart/add with id', idForFallback);
                response = await this.addToCartJsonFallback(idForFallback);
              }
            }

            // If still error, handle and stop
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButton.querySelector('span').classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            }

            if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error) {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            }
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
            // Debug: log cart-add response so we can verify gift-box lines in cart state
            window.logGiftboxDebug('Cart add response', response);
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      async addMonogramProductToCart(groupId) {
        // Prevent multiple simultaneous calls for the same groupId
        const pendingKey = `_personalisation_pending_${groupId}`;
        if (this[pendingKey]) {
          return; // Already processing this groupId
        }
        this[pendingKey] = true;

        try {
          // First, check if a personalization addon already exists for this groupId
          const cartResp = await fetch('/cart.js', {
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
          });
          if (!cartResp.ok) {
            throw new Error(`/cart.js returned ${cartResp.status}`);
          }
          const cart = await cartResp.json();

          // Check if personalization addon already exists for this groupId
          const existingPersonalisation = cart.items.find(
            (item) =>
              item.properties &&
              item.properties._groupId === String(groupId) &&
              item.properties._addon === 'true' &&
              item.properties._addonType === 'Personalisation'
          );

          if (existingPersonalisation) {
            // Personalization already exists for this groupId, don't add again
            return;
          }

          const productId = document.querySelector('personalisation-drawer').dataset.monogramProduct;
          const quantity = 1;

          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify({
              id: parseInt(productId),
              quantity: quantity,
              properties: {
                _groupId: groupId,
                _addon: true,
                _addonType: 'Personalisation',
              },
            }),
          });

          if (response.ok) {
            const responseData = await response.json();
            if (!responseData.status && !this.error) {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                productVariantId: productId,
              });
              await window.updateCartItemCount();
            }
          }
        } catch (e) {
          console.error('Error adding monogram product to cart:', e);
        } finally {
          // Clear the pending flag
          this[pendingKey] = false;
        }
      }

      /**
       * Fallback add-to-cart using JSON body. Some browsers/sites can be picky
       * with multipart FormData; this tries the JSON endpoint shape.
       */
      addToCartJsonFallback(variantId) {
        const sections = this.cart
          ? this.cart.getSectionsToRender().map((section) => section.id)
          : undefined;
        const sectionsUrl = window.location.pathname + window.location.search;

        return fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({
            id: parseInt(variantId, 10),
            quantity: 1,
            ...(sections ? { sections } : {}),
            sections_url: sectionsUrl,
          }),
        }).then(async (res) => {
          const txt = await res.clone().text();
          window.logGiftboxDebug('JSON fallback /cart/add response', { ok: res.ok, status: res.status, body: txt });
          try { return JSON.parse(txt); } catch(_) { return { status: !res.ok, description: txt }; }
        });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
    }
  );
}
 /**
       * Fetches Shopify's AJAX cart object and updates
       * .drawer__heading .item_count to show the current items_count.
       */
 window.updateCartItemCount = async function () {
  // Add cache-busting to prevent stale cart count when returning from checkout
  const cacheBuster = `?_=${Date.now()}`;
  
  // First, update the cart-icon-bubble section to ensure it's fresh
  try {
    const cartIconBubbleEl = document.getElementById('cart-icon-bubble');
    if (cartIconBubbleEl) {
      const cacheBusterSection = `&_=${Date.now()}`;
      const sectionResponse = await fetch(`${routes.cart_url}?sections=cart-icon-bubble${cacheBusterSection}`, {
        cache: 'no-store',
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (sectionResponse.ok) {
        const sectionText = await sectionResponse.text();
        const sectionData = JSON.parse(sectionText);
        const sectionHTML = sectionData['cart-icon-bubble'];
        if (sectionHTML) {
          const parser = new DOMParser();
          const doc = parser.parseFromString(sectionHTML, 'text/html');
          const newContent = doc.querySelector('.shopify-section');
          if (newContent && cartIconBubbleEl.querySelector('.shopify-section')) {
            cartIconBubbleEl.querySelector('.shopify-section').innerHTML = newContent.innerHTML;
          } else if (newContent) {
            cartIconBubbleEl.innerHTML = newContent.outerHTML;
          }
        }
      }
    }
  } catch (err) {
    console.warn('Failed to update cart-icon-bubble section:', err);
  }
  
  // Then update the count directly from cart.js
  fetch(`/cart.js${cacheBuster}`, {
    method: 'GET',
    cache: 'no-store',
    headers: {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
  })
    .then((res) => res.json())
    .then((cart) => {
      // cart.items_count holds the total number of items in the cart
      const count = cart.item_count;
      const drawerCountEl = document.querySelector('.drawer__heading .item_count');
      if (drawerCountEl) {
        drawerCountEl.innerHTML = `(${count})`;
      }
      const bubble = document.querySelector('.cart-count-bubble');
      if (bubble) {
        // First span: visible bubble (aria-hidden)
        const visibleSpan = bubble.querySelector('span[aria-hidden="true"]');
        // Second span: visually-hidden screen-reader text
        const hiddenSpan  = bubble.querySelector('.visually-hidden');

        if (visibleSpan) {
          visibleSpan.textContent = count;
        }
        if (hiddenSpan) {
          // Use singular "item" if count === 1, plural "items" otherwise
          const suffix = count === 1 ? 'item' : 'items';
          hiddenSpan.textContent = `${count} ${suffix}`;
        }
      } else if (count > 0) {
        // If bubble doesn't exist but cart has items, we need to add it
        // This handles the case where cart was empty when page loaded but now has items
        const cartIconBubbleEl = document.getElementById('cart-icon-bubble');
        if (cartIconBubbleEl) {
          // Re-fetch the section to get the bubble
          fetch(`${routes.cart_url}?sections=cart-icon-bubble&_=${Date.now()}`, {
            cache: 'no-store',
            headers: { 'X-Requested-With': 'XMLHttpRequest' }
          })
            .then(r => r.text())
            .then(text => {
              const data = JSON.parse(text);
              const html = data['cart-icon-bubble'];
              if (html) {
                const parser = new DOMParser();
                const doc = parser.parseFromString(html, 'text/html');
                const newContent = doc.querySelector('.shopify-section');
                if (newContent) {
                  const existingSection = cartIconBubbleEl.querySelector('.shopify-section');
                  if (existingSection) {
                    existingSection.innerHTML = newContent.innerHTML;
                  } else {
                    cartIconBubbleEl.innerHTML = newContent.outerHTML;
                  }
                }
              }
            })
            .catch(() => {});
        }
      }
    })
    .catch((err) => {
      console.error('Failed to fetch cart.js:', err);
    });
}

// Refresh cart count when page is loaded from cache (back button from checkout)
if (typeof window !== 'undefined') {
  // Immediate refresh on page load (handles back button case)
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      if (window.updateCartItemCount) {
        window.updateCartItemCount();
      }
    });
  } else {
    // DOM already loaded, refresh immediately
    if (window.updateCartItemCount) {
      window.updateCartItemCount();
    }
  }

  window.addEventListener('pageshow', (event) => {
    // event.persisted is true when page is loaded from cache (back button)
    if (event.persisted && window.updateCartItemCount) {
      // Immediate refresh for back button
      window.updateCartItemCount();
    }
  });

  // Also refresh when page becomes visible (returning from checkout in another tab)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && window.updateCartItemCount) {
      // Small delay to ensure page is fully loaded
      setTimeout(() => {
        window.updateCartItemCount();
      }, 100);
    }
  });
}